// calculation-state.ts
import { CALCULATION_DEFAULT_COLUMNS, ColumnDefinition, TAIL_COLUMNS, mapToInactiveDefinition } from "../constants";
import { CalculationHeaderApi, CalculationLineApi, CalculationMetaApi, CalculationPercentagesApi, CalculationTailApi, TemplateCategoryApi, ValueSetKey } from "../types";

export const DEFAULT_HOUR_RATE = 40;

// Define types
export type TailLineType = '€' | '%' | 't';

/**
 * Dimensions of calculation lines that can be summed to (sub)total
 * Keys 'net' and 'gross' are mandatory and can be aggregated over all lines
 */
export type MandatorySumDimension = 'norm' | 'net' | 'gross' | 'unitPrice';

/**
 * Dimensions of calculation lines that can be summed to (sub)total
 * Key 'hours' is optional
 */
export type OptionalSumDimensions = 'hours';

/**
 * Mandatory aggregated values for a set of calculation lines
 * This will typically be lines with same category or subcategory
 */
export type MandatoryAggregatedValues = { [dimension in MandatorySumDimension]: number | undefined };

/**
 * Mandatory aggregated values for a set of calculation lines
 * This will typically be lines with same category or subcategory
 */
export type OptionalAggregatedValues = { [dimension in OptionalSumDimensions]?: number | undefined };

/**
 * Aggregation of values will be calculated for every cost dimension separately
 */
export type CostDimension = ValueSetKey | 'all';

export type Aggregation = {
    [dimension in CostDimension]: MandatoryAggregatedValues & OptionalAggregatedValues;
};

/**
 * Add one or more aggregation object values to another aggregation object
 * @param target the object that will be returned after the values of the other objects are added 
 * @param sources the objects with values to add to the target object
 * @returns the target object with updated values
 */
const addMandatoryAggregatedValues = (target: MandatoryAggregatedValues, ...sources: MandatoryAggregatedValues[]): MandatoryAggregatedValues => {
    sources.forEach(source => {
        // target.norm = addOptionalValues(target.norm, source.norm);
        target.net = addOptionalValues(target.net, source.net);
        target.gross = addOptionalValues(target.gross, source.gross);
        // target.unitPrice = addOptionalValues(target.unitPrice, source.unitPrice);
    });

    return target;
};

/**
 * Add one or more aggregation object values to another aggregation object
 * @param target the object that will be returned after the values of the other objects are added 
 * @param sources the objects with values to add to the target object
 * @returns the target object with updated values
 */
const addOptionalAggregatedValues = (target: OptionalAggregatedValues, ...sources: OptionalAggregatedValues[]): OptionalAggregatedValues => {
    sources.forEach(source => {
        target.hours = addOptionalValues(target.hours, source.hours);
    });
    return target;
};

/**
 * Add one or more aggregation object values to another aggregation object
 * @param target the object that will be returned after the values of the other objects are added 
 * @param sources the objects with values to add to the target object
 * @returns the target object with updated values
 */
export const mergeAggregatedValues = (target: Aggregation, ...sources: Aggregation[]): Aggregation => {
    sources.forEach(source => {
        target.hours = {
            ...addMandatoryAggregatedValues(target.hours, source.hours),
            ...addOptionalAggregatedValues(target.hours, source.hours)
        };
        target.material = addMandatoryAggregatedValues(target.material, source.material);
        target.equipment = addMandatoryAggregatedValues(target.equipment, source.equipment);
        target.subcontracting = addMandatoryAggregatedValues(target.subcontracting, source.subcontracting);
        target.all = addMandatoryAggregatedValues(target.all, source.all)
    });

    return target;
};

/**
 * Combination of optional and mandatory aggregated values for a set of calculation lines
 * This will typically be lines with same category or subcategory
 */
export type AggregatedValues = {
    aggregation?: Aggregation;
};

/**
 * View model line type
 */
export enum ViewModelLineType {
    'CATEGORY' = 'category',
    'SUBCATEGORY' = 'subcategory',
    'SUBTOTAL' = 'subtotal',
    'READ_ONLY' = 'readOnly',
    'TOTAL' = 'total',
    'CALCULATION' = 'calculation',
    'TEXT' = 'text',
    'TAIL_AMOUNT' = 'tailAmount',
    'TAIL_PERCENTAGE' = 'tailPercentage',
    'TAIL_TEXT' = 'tailText',
    'TAIL_PRELIMANARY' = 'tailPrelimanary',
    'TAIL_SUBTOTAL' = 'tailSubtotal',
};

/**
 * Labels in multiple languages
 */
export enum Language { NL = 'nl', EN = 'en' };

export type Translation = Partial<{
    [key in Language]: string;
}>;

/**
 * Warnings for calculation and text lines
 */
export enum WarningType { 'MISSING DESCRIPTION', 'MISSING QUANTITY', 'MISSING NORM' };

// Type guard function to check if an object is CalculationLineApi
export function isCalculationLineApi(line: any): line is CalculationLineApi {
    if (!line) {
        return false;
    }

    return 'code' in line && 'sourceVersion' in line;
}

// Type guard function to check if an object is CalculationTailApi
export function isCalculationTailApi(line: any): line is CalculationTailApi {
    if (!line) {
        return false;
    }

    return 'sourceVersion' in line && !('code' in line);
}

/**
 * Structure of the view model lines
 * Render differences are based on value of type property
 */
export interface ViewModelLine extends AggregatedValues {
    /**
     * View model line type
     */
    type: ViewModelLineType;

    /**
     * Index of the line in the paging graph
     * @deprecated will be removed for smoother rendering when scrolling
     */
    index?: number;

    /**
     * The offset in pixels from the top of the paging graph
     * @deprecated will be removed for smoother rendering when scrolling
     */
    offset?: number;

    /**
     * Some view lines have a direct link to a line in the calculation data
     * A reference to this line is saved here
     */
    line?: CalculationLineApi | CalculationTailApi;

    /**
     * The code for this view line
     * Code is [categoryId].[subcategoryId] where both ids are strings with length === 2
     * For categories, only the categoryId is used
     */
    code?: string;

    /**
     * Optional description for rendering the line
     */
    description?: string;

    /**
     * Warnings for this line
     * Mainly used for rendering. Merged to one list for all lines in state
     */
    warnings?: WarningType[];

    section: 'calculation' | 'tail';

    hidden?: boolean;
}

// Types for selection
export type CalculationSelection = {
    origin?: CellSelection;
    current: CellSelection;
    selectedLines?: (CalculationLineApi | CalculationTailApi)[];
};

export type CellSelection = {
    row: number;
    column: number;
};

export type ColumnDefinitions = { [columnName in ViewModelLineType]: ColumnDefinition };

export interface CalculationState extends AggregatedValues {
    /**
     * The template contains all categories and subcategories for this calculation type
     * Including default lines for subcategories
     */
    template: TemplateCategoryApi[];

    templateDict: { [code: string]: string };

    /**
     * The meta object contains additional information about the calculation and project
     */
    meta?: CalculationMetaApi;

    /**
     * The lines make up the entire calculation. It contains the minimal information
     * without any process information like net and gross amounts for MAMO subject, subcategories
     * categories and grand totals.
     * Also, category and subcategory labels are not saved with the calculation data. They can be
     * retrieved from the template. Default labour rates are saved in the header
     */
    lines: CalculationLineApi[];

    /**
     * Tried to leave the view model out of the application state
     * But complicating things with that decision. The view model contains important state information
     * that is not found in the former model, like selected row and column id
     * Now, bringing it here. And rebuilding the view model as an effect
     */
    viewModelLines: ViewModelLine[];

    /**
     * Keeping track of newly created lines in the calculation
     *
     * @type {Record<string, boolean>}
     * @memberof CalculationState
     */
    creatingIds: Record<string, boolean>;

    /**
     * Keeping track of lines that are changed during the creation process
     *
     * @type {Record<string, boolean>}
     * @memberof CalculationState
     */
    staleIds: Record<string, boolean>;

    /**
     * At this moment, the header only contains labour hour rates. So here, the property name 'hourRates' is
     * chosen for the header section of the calculation
     */
    hourRates: CalculationHeaderApi[];

    /**
     * The tail contains the tail section of the calculation
     */
    tail?: CalculationTailApi[];

    /**
     * Default percentages for MAMO categories are saved to the meta object. Since it will be used as trigger
     * it is used as a separated property in the state object
     */
    defaultPercentages?: CalculationPercentagesApi;

    /**
     * Keep track of the loading status of a calculation, prevend parallel loading
     */
    loading: boolean;

    /**
     * Holds the last error
     */
    error: any;

    /**
     * The selected line(s) in the active calculation
     */
    currentSelection?: CalculationSelection;

    /**
     * Second calculation, now only used to import lines
     */
    referenceCalculation?: CalculationLineApi[];

    /**
     * This is still a design error
     * Only current version of the loaded calculation is available
     * So when doing the initial load (versions are only accessible through the current version),
     * save the version number
     */
    maxVersion?: number;

    /**
     * All warnings for all calculation lines
     */
    warnings?: WarningType[];

    columns: ColumnDefinitions;

    aggregationOnLoad?: Aggregation;

    show: 'calculation' | 'all' | 'tail';

    tableLayout: 'default' | 'condensed';

    sideMenuOpen: boolean;
}

/**
 * Shorthand notation for inital values of aggregation
 */
export const createEmptyAggregationValues = (): MandatoryAggregatedValues & OptionalAggregatedValues => ({
    gross: undefined,
    net: undefined,
    norm: undefined,
    unitPrice: undefined
})

export const lineCostDimensionToAggregatedValues = (line: CalculationLineApi, dimension: ValueSetKey, hourRates: CalculationHeaderApi[], defaultPercentages?: CalculationPercentagesApi): MandatoryAggregatedValues & OptionalAggregatedValues => {
    if (!line || !line[dimension]) {
        return createEmptyAggregationValues();
    }

    // Labour costs are calculated differntly
    // The line quantity is multiplied by the hours norm, resulting in the number of hours needed
    // And not the total costs, like with other dimensions
    // The hours calculated have to be multiplied with the costs per hour, to get the costs
    // Although net is present in data model, it is recalculated every time

    // First steps in calculation are same for all dimensions
    const { norm, percent } = line[dimension];

    // Get the default percentage for this dimension
    const defaultPercentage = defaultPercentages?.[dimension];

    let net = norm !== undefined && line.quantity !== undefined && !Number.isNaN(line.quantity) ? norm * Number(line.quantity) : undefined;
    let hours = undefined;

    // For hours, multiply with hourly rate
    if (dimension === 'hours' && net !== undefined) {
        // Number of hours is result of norm times quantity
        hours = net;

        // Multiply hours with rate to get the amount
        const rate = hourRates.find(rate => rate.id === line.rate)?.value ?? DEFAULT_HOUR_RATE;

        net *= rate;
    }

    const gross = net !== undefined ? net * (1 + (percent ?? defaultPercentage ?? 0)) : undefined;

    // const unitPrice = net !== undefined && !Number.isNaN(line.quantity) && Number(line.quantity) ? net / Number(line.quantity) : undefined;
    const unitPrice = gross !== undefined && !Number.isNaN(line.quantity) && Number(line.quantity) ? gross / Number(line.quantity) : undefined;

    return {
        gross: gross,
        net: net,
        norm: norm,
        hours: hours,
        unitPrice: unitPrice
    }
}

/**
 * Get the calculated costs for a single calculation line
 * This will return norm and hours as original values
 * And also calculate net and gross costs for every cost dimension
 * @param line calculation line
 * @returns aggregated object with calculted values
 */
export const calculateCostsForCalculationLine = (line: CalculationLineApi, rates: CalculationHeaderApi[] = [], defaultPercentages?: CalculationPercentagesApi): Aggregation => {
    const result = createEmptyAggregation();

    result.all = (['hours', 'material', 'equipment', 'subcontracting'] as ValueSetKey[]).reduce((aggregated, dimension) => {
        // Calculate the values for this dimension
        const dimensionValue = lineCostDimensionToAggregatedValues(line, dimension, rates, defaultPercentages);

        // Assign these values to the result object
        result[dimension] = dimensionValue;

        // Add to all
        aggregated.gross = addOptionalValues(aggregated.gross, dimensionValue.gross);
        aggregated.net = addOptionalValues(aggregated.net, dimensionValue.net);
        aggregated.norm = addOptionalValues(aggregated.norm, dimensionValue.norm);
        aggregated.unitPrice = addOptionalValues(aggregated.unitPrice, dimensionValue.unitPrice);
        aggregated.hours = addOptionalValues(aggregated.hours, dimensionValue.hours);

        return aggregated;
    }, createEmptyAggregationValues())

    return result;
}

/**
 * Adds two values that can be number or undefined.
 * If both values are undefined, returns undefined.
 * Otherwise, returns the sum of the defined values, treating undefined as 0.
 * @param value1 - The first value to add
 * @param value2 - The second value to add
 * @returns The sum of the two values or undefined if both are undefined
 */
const addOptionalValues = (value1: number | undefined, value2: number | undefined): number | undefined => {
    if (value1 === undefined && value2 === undefined) {
        return undefined;
    }
    return (value1 ?? 0) + (value2 ?? 0);
};

export const createEmptyAggregation = (): Aggregation => ({
    equipment: createEmptyAggregationValues(),
    hours: createEmptyAggregationValues(),
    material: createEmptyAggregationValues(),
    subcontracting: createEmptyAggregationValues(),
    all: createEmptyAggregationValues()
})

export const initialCalculationState: CalculationState = {
    template: [],
    templateDict: {},
    lines: [],
    viewModelLines: [],
    hourRates: [],
    loading: false,
    error: null,
    aggregation: createEmptyAggregation(),
    columns: {
        calculation: CALCULATION_DEFAULT_COLUMNS,
        subcategory: mapToInactiveDefinition(CALCULATION_DEFAULT_COLUMNS),
        category: mapToInactiveDefinition(CALCULATION_DEFAULT_COLUMNS),
        subtotal: mapToInactiveDefinition(CALCULATION_DEFAULT_COLUMNS),
        text: mapToInactiveDefinition(CALCULATION_DEFAULT_COLUMNS, ['description', 'quantity', 'unit']),
        total: mapToInactiveDefinition(CALCULATION_DEFAULT_COLUMNS),
        readOnly: mapToInactiveDefinition(CALCULATION_DEFAULT_COLUMNS),
        tailAmount: TAIL_COLUMNS,
        tailPercentage: TAIL_COLUMNS,
        tailText: TAIL_COLUMNS,
        tailPrelimanary: mapToInactiveDefinition(TAIL_COLUMNS),
        tailSubtotal: mapToInactiveDefinition(TAIL_COLUMNS),
    },
    show: 'all',
    tableLayout: 'default',
    creatingIds: {},
    staleIds: {},
    sideMenuOpen: true,
};
